// layout/MainLayout.js
import React, { useState, useEffect, useMemo } from 'react';
import { styled, createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import { useAppearance, resolveThemeMode } from '../context/AppearanceContext';
import CssBaseline from '@mui/material/CssBaseline';
import '../styles/modern-theme.css';
import logo from '../assets/images/logo.png';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InboxIcon from '@mui/icons-material/Inbox';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PublicIcon from '@mui/icons-material/Public';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReportIcon from '@mui/icons-material/Report';
import ListSubheader from '@mui/material/ListSubheader';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// Olymp Icons
import HomeIcon from '@mui/icons-material/Home';
import FolderIcon from '@mui/icons-material/Folder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GroupIcon from '@mui/icons-material/Group';
import MapIcon from '@mui/icons-material/Map';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import Link from '@mui/material/Link';
import { useNavigate, useLocation } from 'react-router-dom';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { getResolvedOlympAccess } from '../utils/olympAccess';

const drawerWidth = 240;

// AppBar, Drawer und weitere stilistische Konfigurationen hier
function Copyright(props) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" {...props}>
        {'Copyright © '}
        <Link color="inherit" href="https://argus-health.de" target="_blank" rel="noopener noreferrer">
          argus-health.de
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
      </Typography>
    );
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

const MainLayout = ({ children }) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { appearance } = useAppearance();
  const themeMode = resolveThemeMode(appearance.theme);

  // User role menu state
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(() => {
    return localStorage.getItem('olymp_demo_role') || getResolvedOlympAccess().role || 'user';
  });
  const userMenuOpen = Boolean(userMenuAnchorEl);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleRoleChange = (role) => {
    setCurrentUserRole(role);
    localStorage.setItem('olymp_demo_role', role);
    handleUserMenuClose();
    window.location.reload();
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'user':
        return t('layout.userRole.user');
      case 'data_steward':
        return t('layout.userRole.dataSteward');
      case 'admin':
        return t('layout.userRole.admin');
      default:
        return t('layout.userRole.user');
    }
  };

  // Nur Debug-Ausgabe, keine automatische Umleitung
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    console.log('Token vorhanden:', !!token);
    // Wir leiten nicht automatisch um, damit die Seiten für Demo-Zwecke zugänglich bleiben
  }, []);
  
  // Logout-Funktion
  const handleLogout = () => {
    try {
      console.log('Logout-Funktion ausgelöst');
      
      // Token aus dem lokalen Speicher entfernen
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      console.log('Token entfernt, navigiere zu /login');
      
      // Zur Login-Seite umleiten
      window.location.href = '/login';
    } catch (error) {
      console.error('Fehler beim Logout:', error);
    }
  };

  // Anzahl neuer Anfragen für Badge (temporär hardcoded für Demo)
  const newRequestsCount = 2;

  // Olymp Menüpunkte
  const olympMenuItems = [
    { text: t('layout.menu.home'), icon: <HomeIcon />, path: '/' },
    { text: t('layout.menu.catalog'), icon: <FolderIcon />, path: '/catalog' },
    { text: t('layout.menu.cohorts'), icon: <GroupIcon />, path: '/cohorts' },
    { text: t('layout.menu.requests'), icon: <AssessmentIcon />, path: '/requests' },
  ];

  const commonMenuItems = [
    { text: t('layout.menu.settings'), icon: <SettingsIcon />, path: '/settings' },
    { text: t('layout.menu.adminPanel'), icon: <SecurityIcon />, path: '/settings', state: { openTab: 5 } },
    { text: t('layout.menu.help'), icon: <HelpIcon />, path: '/help' },
  ];

  const modernTheme = useMemo(() => {
    const primaryColor = appearance.primaryColor || '#FF7A00';
    const fontScale = appearance.fontScale ?? 1;
    const animationsEnabled = appearance.animationsEnabled !== false;
    const isDark = themeMode === 'dark';

    return createTheme({
      palette: {
        mode: themeMode,
        primary: {
          main: primaryColor,
          light: primaryColor,
          dark: primaryColor,
        },
        secondary: {
          main: '#00B8D4',
          light: '#62EBFF',
          dark: '#0088A3',
        },
        background: isDark
          ? { default: '#121212', paper: '#1e1e1e' }
          : { default: '#F8F9FA', paper: '#ffffff' },
        text: isDark
          ? { primary: '#e2e8f0', secondary: '#a0aec0' }
          : { primary: '#2D3748', secondary: '#718096' },
        error: { main: '#F56565' },
        warning: { main: '#F6AD55' },
        info: { main: '#4299E1' },
        success: { main: '#48BB78' },
      },
      typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14 * fontScale,
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        button: { fontWeight: 600, textTransform: 'none' },
      },
      shape: { borderRadius: 8 },
      transitions: {
        duration: {
          shortest: animationsEnabled ? 150 : 0,
          shorter: animationsEnabled ? 200 : 0,
          short: animationsEnabled ? 250 : 0,
          standard: animationsEnabled ? 300 : 0,
          complex: animationsEnabled ? 375 : 0,
          enteringScreen: animationsEnabled ? 225 : 0,
          leavingScreen: animationsEnabled ? 195 : 0,
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              borderRadius: 8,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            },
          },
        },
      },
    });
  }, [themeMode, appearance.primaryColor, appearance.fontScale, appearance.animationsEnabled]);

  return (
    <ThemeProvider theme={modernTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="absolute"
          open={open}
          className="app-header"
          sx={{
            backgroundColor: 'background.paper',
            boxShadow: 'var(--box-shadow-sm)',
            backgroundImage: (theme) => themeMode === 'dark' ? 'none' : `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.07)})`,
          }}
        >
          <Toolbar sx={{ pr: '24px' }}>
            <IconButton
              edge="start"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={(theme) => ({
                marginRight: '36px',
                ...(open && { display: 'none' }),
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                }
              })}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography
              component="h1"
              variant="h6"
              noWrap
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                color: 'text.primary' // Dunkelgrau statt Orange
              }}
            >
              {t('layout.appTitle')}
              {process.env.REACT_APP_ENVIRONMENT && (
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: process.env.REACT_APP_ENVIRONMENT === 'DEV' ? '#4299E1' : '#F6AD55',
                  color: 'white',
                  letterSpacing: '1px',
                  marginLeft: '8px',
                }}>
                  {process.env.REACT_APP_ENVIRONMENT}
                </span>
              )}
            </Typography>

            <Box sx={{ mr: 1.5 }}>
              <LanguageSwitcher />
            </Box>

            {/* Auth-backed user role indicator */}
            <Tooltip title={t('layout.userRole.currentRole', { role: getRoleLabel(currentUserRole) })}>
              <IconButton
                color="inherit"
                onClick={handleUserMenuClick}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
                aria-label={t('layout.userRole.menuTitle')}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={userMenuAnchorEl}
              open={userMenuOpen}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="textSecondary">
                  Demo: Rolle wählen
                </Typography>
              </Box>
              <MenuItem onClick={() => handleRoleChange('user')} selected={currentUserRole === 'user'}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <PersonIcon fontSize="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {t('layout.userRole.user')}
                  </Typography>
                  {currentUserRole === 'user' && <CheckCircleIcon fontSize="small" color="success" />}
                </Box>
              </MenuItem>
              <MenuItem onClick={() => handleRoleChange('data_steward')} selected={currentUserRole === 'data_steward'}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <AssignmentTurnedInIcon fontSize="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {t('layout.userRole.dataSteward')}
                  </Typography>
                  {currentUserRole === 'data_steward' && <CheckCircleIcon fontSize="small" color="success" />}
                </Box>
              </MenuItem>
              <MenuItem onClick={() => handleRoleChange('admin')} selected={currentUserRole === 'admin'}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <SecurityIcon fontSize="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {t('layout.userRole.admin')}
                  </Typography>
                  {currentUserRole === 'admin' && <CheckCircleIcon fontSize="small" color="success" />}
                </Box>
              </MenuItem>
            </Menu>

            <IconButton color="inherit">
              <Badge badgeContent={4} color="primary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{
                ml: 1,
                color: 'text.secondary',
                '&:hover': { color: 'error.main' }
              }}
              aria-label="logout"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer 
          variant="permanent" 
          open={open}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: 'background.paper',
              boxShadow: 'var(--box-shadow)',
              borderRight: 'none',
            }
          }}
        >
          <Box 
            className="sidebar-brand"
            sx={(theme) => ({
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.1),
            })}
          >
            {open && (
              <div className="logo-container" style={{ marginLeft: 0 }}>
                <img
                  src={logo}
                  alt="ARGUS Logo"
                  style={{ height: 40 }}
                />
              </div>
            )}
            <IconButton 
              onClick={toggleDrawer} 
              sx={(theme) => ({
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                },
                ...(open ? { mr: 1 } : { mx: 'auto' })
              })}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Divider />
          <List
            component="nav"
            sx={(theme) => ({
              '& .MuiListItemButton-root': {
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    fontWeight: 600,
                    color: 'primary.main',
                  },
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: 40,
                color: 'text.secondary',
              },
              '& .MuiListSubheader-root': {
                backgroundColor: 'transparent',
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '1.5',
                px: 2,
                py: 1,
              },
            })}
          >
            {/* Olymp Section */}
            {open && (
                <ListSubheader component="div" disableSticky>
                {t('layout.nav.navigation')}
                </ListSubheader>
            )}
            {olympMenuItems.map((item) => (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText primary={item.text} />}
                {item.badge && (
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: open ? 24 : 20,
                      height: open ? 24 : 20,
                      minWidth: open ? 24 : 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      ml: open ? 1 : 'auto',
                      mr: open ? 0 : 'auto',
                      position: open ? 'relative' : 'absolute',
                      right: open ? 'auto' : 10,
                      whiteSpace: 'nowrap',
                      overflow: 'visible'
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
              </ListItemButton>
            ))}

            {/* Common Section */}
            {open && (
              <>
                <Box sx={{ my: 1 }}>
                  <Divider />
                </Box>
                <ListSubheader component="div" disableSticky>
                  {t('layout.nav.general')}
                </ListSubheader>
              </>
            )}
            {commonMenuItems.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => navigate(item.path, item.state ? { state: item.state } : undefined)}
                selected={location.pathname === item.path && !item.state}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText primary={item.text} />}
              </ListItemButton>
            ))}

          </List>
          
          {open && (
            <Box sx={(theme) => ({ mt: 'auto', mb: 2, mx: 2, p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05) })}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('layout.support.needHelp')}
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                <Link href="#" underline="none" color="inherit">
                  {t('layout.support.contactSupport')}
                </Link>
              </Typography>
            </Box>
          )}
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            minHeight: '100vh',
          }}
          className="content-area"
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {children}
            <Copyright sx={{ pt: 4 }} />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;
